import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import { useAuth } from 'src/hooks/use-auth';
import { useSocketEvent } from 'src/hooks/use-socket-event';
import { chatApi } from 'src/api/chat/chatService';
import { formatDate } from 'src/utils/format-date';
import { setDestinoChatActivo, limpiarDestinoChat } from 'src/utils/push-active';
import { MarkdownEditor } from 'src/components/markdown-editor';
import { MarkdownText } from 'src/components/markdown-text';
import { toast } from 'react-hot-toast';

const tipoLabels = {
  fideicomiso_flujos: 'Fideicomiso de Flujos',
  osce: 'OSCE',
  bienes_servicios: 'Bienes & Servicios',
  obras_impuestos: 'Obras por Impuestos',
  fondo_mivivienda: 'Fondo Mi Vivienda',
  contrato_mutuo: 'Contrato de Mutuo',
};

const subTipoLabels = { bienes: 'Bienes', servicios: 'Servicios', obras: 'Obras' };

const estadoLabels = {
  pendiente_revision: 'Enviada',
  en_revision: 'En Evaluación',
  en_observacion: 'Observada: garantes',
  reenviado: 'Enviada',
  rechazado: 'Observada: pyme',
  validado: 'Aprobada',
  solicitado: 'Solicitado',
  propuesta_enviada: 'Propuesta Enviada',
  estructurado: 'Estructurado',
  aceptado: 'Aceptado',
  en_llamado: 'En Llamado',
  activo: 'Activo',
  cobrando: 'Cobrando',
  liquidado: 'Liquidado',
  cancelado: 'Cancelado',
};

const estadoColors = {
  pendiente_revision: 'warning',
  en_revision: 'info',
  en_observacion: 'error',
  reenviado: 'warning',
  rechazado: 'warning',
  validado: 'success',
  solicitado: 'info',
  propuesta_enviada: 'info',
  estructurado: 'secondary',
  aceptado: 'success',
  en_llamado: 'warning',
  activo: 'success',
  cobrando: 'info',
  liquidado: 'default',
  cancelado: 'error',
};

const nombrePyme = (op) => {
  const n = op?.pyme;
  if (!n) return 'PYME';
  return [n.business || '', `${n.name || ''} ${n.lastname || ''}`.trim()]
    .filter(Boolean)
    .join(' · ');
};

const tipoTexto = (op) => {
  if (!op) return '';
  const base = tipoLabels[op.tipo_inversion] || op.tipo_inversion;
  if (op.tipo_inversion === 'osce' && op.sub_tipo) {
    return `${base} · ${subTipoLabels[op.sub_tipo] || op.sub_tipo}`;
  }
  return base;
};

const agrupar = (mensajes = []) => {
  const resultado = [];
  [...mensajes]
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
    .forEach((m) => {
      const clave = formatDate(m.fecha);
      const ultimo = resultado[resultado.length - 1];
      if (ultimo && ultimo.fecha === clave) ultimo.mensajes.push(m);
      else resultado.push({ fecha: clave, mensajes: [m] });
    });
  return resultado;
};

const hora = (f) =>
  f ? new Date(f).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }) : '';

const ThreadView = ({ mensajes = [], esMio }) => {
  const ref = useRef(null);
  const bloques = useMemo(() => agrupar(mensajes), [mensajes]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
    });
  }, [bloques]);

  return (
    <Box
      ref={ref}
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 1.5,
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'background.default' : 'grey.50'),
      }}
    >
      {bloques.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center', py: 3 }}
        >
          Inicia la conversación
        </Typography>
      )}
      {bloques.map((bloque) => (
        <Box
          key={bloque.fecha}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mb: 0.5 }}
          >
            {bloque.fecha}
          </Typography>
          {bloque.mensajes.map((m, idx) => {
            const mio = esMio(m);
            const previo = idx > 0 ? bloque.mensajes[idx - 1] : null;
            const mostrarAutor =
              !mio &&
              (!previo || previo.autor !== m.autor || previo.autor_nombre !== m.autor_nombre);
            return (
              <Box
                key={m._id || idx}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: mio ? 'flex-end' : 'flex-start',
                }}
              >
                {mostrarAutor && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mx: 1, mb: 0.25 }}
                  >
                    {m.autor_nombre || 'Mensaje'}
                  </Typography>
                )}
                <Box
                  sx={{
                    maxWidth: '80%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    bgcolor: mio
                      ? 'primary.main'
                      : (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                    color: mio ? 'primary.contrastText' : 'text.primary',
                    borderBottomRightRadius: mio ? 4 : 12,
                    borderBottomLeftRadius: mio ? 12 : 4,
                  }}
                >
                  <Box
                    sx={{
                      '& p': { m: 0 },
                      '& a': {
                        color: mio ? 'inherit !important' : (theme) => theme.palette.primary.main,
                        textDecoration: mio ? 'underline' : 'none',
                      },
                    }}
                  >
                    <MarkdownText>{m.mensaje}</MarkdownText>
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.25,
                      opacity: 0.75,
                      fontSize: 10,
                    }}
                  >
                    {hora(m.fecha)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

const ComposerView = ({ draft, onChange, onSend, sending, editorKey, disabled }) => {
  return (
    <>
      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <MarkdownEditor
            key={editorKey}
            value={draft}
            onChange={onChange}
            placeholder="Escribe un mensaje..."
            minRows={1}
            onSubmit={onSend}
          />
          <Stack
            direction="row"
            justifyContent="flex-end"
          >
            <Button
              variant="contained"
              color="primary"
              onClick={onSend}
              disabled={sending || disabled || !draft.trim()}
              sx={{ minWidth: 0, px: 2 }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

const TabConteo = ({ count, activo }) => {
  if (!count) return null;
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        px: 0.6,
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        bgcolor: activo ? 'common.white' : 'error.main',
        color: activo ? 'error.main' : 'common.white',
      }}
    >
      {count > 99 ? '99+' : count}
    </Box>
  );
};

const ConversacionCard = ({ item, activa, onClick, primario, secundario, tipo, no_leidos }) => {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.75,
        cursor: 'pointer',
        bgcolor: activa ? 'action.selected' : 'background.paper',
        borderColor: activa ? 'primary.main' : 'divider',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '&:hover': { backgroundColor: 'action.hover', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: no_leidos ? 700 : 500 }}
        >
          {primario}
        </Typography>
        {item.estado ? (
          <Chip
            label={estadoLabels[item.estado] || item.estado}
            color={estadoColors[item.estado] || 'default'}
            size="small"
            variant="outlined"
          />
        ) : null}
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        sx={{ mt: 0.5 }}
      >
        {secundario || 'Conversación abierta'}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 0.5 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
        >
          {tipo}
        </Typography>
        {no_leidos > 0 && (
          <Badge
            badgeContent={no_leidos}
            color="error"
          />
        )}
      </Stack>
    </Paper>
  );
};

export const ChatLauncher = () => {
  const { user } = useAuth();
  const soyPyme = user?.role === 'pyme';
  const soyInv = user?.role === 'inversionista';
  const soyGarante = user?.role === 'garante';
  const esParticipante = soyPyme || soyInv || soyGarante;

  const [open, setOpen] = useState(false);
  const [canal, setCanal] = useState('pyme'); // pyme | inv (solo garante alterna)

  // Pyme channel
  const [conversaciones, setConversaciones] = useState([]);
  const [hilo, setHilo] = useState(null);
  const [hiloCargando, setHiloCargando] = useState(false);

  // Investor channel
  const [convInv, setConvInv] = useState([]);
  const [hiloInv, setHiloInv] = useState(null);
  const [hiloInvCargando, setHiloInvCargando] = useState(false);
  const [invVista, setInvVista] = useState('lista'); // lista | proyectos
  const [invSel, setInvSel] = useState(null);
  const [busquedaInv, setBusquedaInv] = useState('');

  const [draft, setDraft] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [confirmarObs, setConfirmarObs] = useState(false);
  const [marcandoObs, setMarcandoObs] = useState(false);
  const [confirmarRechazo, setConfirmarRechazo] = useState(false);
  const [rechazando, setRechazando] = useState(false);

  const canalActivo = soyInv ? 'inv' : canal;

  useEffect(() => {
    if (!open) {
      limpiarDestinoChat();
      return;
    }
    if (hilo) {
      setDestinoChatActivo({ operacion_id: hilo.operacion_id || hilo._id });
    } else if (hiloInv) {
      setDestinoChatActivo({
        operacion_id: hiloInv.operacion_id,
        inversionista_id: hiloInv.inversionista_id,
      });
    } else {
      limpiarDestinoChat();
    }
  }, [open, hilo, hiloInv]);

  const totalNoLeidos = useMemo(
    () =>
      conversaciones.reduce((s, c) => s + (c.no_leidos || 0), 0) +
      convInv.reduce((s, c) => s + (c.no_leidos || 0), 0),
    [conversaciones, convInv]
  );

  const investorsAgrupados = useMemo(() => {
    const mapa = {};
    (convInv || []).forEach((c) => {
      const id = c.inversionista_id;
      const nombre = c.inversionista?.nombre || 'Inversionista';
      if (!mapa[id]) {
        mapa[id] = { inversionista_id: id, nombre, proyectos: [], no_leidos: 0 };
      }
      mapa[id].proyectos.push(c);
      mapa[id].no_leidos += c.no_leidos || 0;
    });
    const lista = Object.values(mapa).map((inv) => ({
      ...inv,
      proyectosConNoLeidos: inv.proyectos.filter((p) => (p.no_leidos || 0) > 0).length,
      proyectos: [...inv.proyectos].sort(
        (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)
      ),
    }));
    const q = busquedaInv.trim().toLowerCase();
    return q ? lista.filter((i) => i.nombre.toLowerCase().includes(q)) : lista;
  }, [convInv, busquedaInv]);

  const proyectosDeInv = useMemo(() => {
    if (!invSel) return [];
    return (convInv || []).filter(
      (c) => String(c.inversionista_id) === String(invSel.inversionista_id)
    );
  }, [convInv, invSel]);

  const pymesConNoLeidos = useMemo(
    () => conversaciones.filter((c) => (c.no_leidos || 0) > 0).length,
    [conversaciones]
  );

  const inversionistasConNoLeidos = useMemo(() => {
    if (soyGarante) {
      return investorsAgrupados.filter((i) => i.proyectos.some((p) => (p.no_leidos || 0) > 0))
        .length;
    }
    return convInv.filter((c) => (c.no_leidos || 0) > 0).length;
  }, [soyGarante, investorsAgrupados, convInv]);

  const cargarPyme = useCallback(async () => {
    try {
      const res = await chatApi.listarConversaciones();
      setConversaciones(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const cargarInv = useCallback(async () => {
    try {
      const res = await chatApi.listarInv();
      setConvInv(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!esParticipante) return;
    if (!soyInv) cargarPyme();
    if (soyGarante || soyInv) cargarInv();
  }, [esParticipante, soyInv, soyGarante, cargarPyme, cargarInv]);

  // ---- Sockets ----
  const chatNuevoHandler = useCallback(
    (payload) => {
      const opId = payload?.operacion_id;
      if (!opId || !payload?.mensaje) return;
      setConversaciones((prev) => {
        const existe = prev.some((c) => String(c._id) === String(opId));
        if (!existe) {
          cargarPyme();
          return prev;
        }
        const abierto = hilo && String(hilo.operacion_id || hilo._id) === String(opId);
        return prev.map((c) =>
          String(c._id) === String(opId)
            ? {
                ...c,
                ultimo: payload.mensaje,
                no_leidos: abierto ? 0 : (c.no_leidos || 0) + 1,
              }
            : c
        );
      });
      if (hilo && String(hilo.operacion_id || hilo._id) === String(opId)) {
        setHilo((prev) => {
          const existe = (prev?.mensajes || []).some(
            (m) => String(m._id) === String(payload.mensaje._id)
          );
          if (existe) return prev;
          return { ...prev, mensajes: [...(prev?.mensajes || []), payload.mensaje] };
        });
        chatApi.marcarLeidas(opId).catch(() => {});
      }
    },
    [hilo, cargarPyme]
  );
  useSocketEvent('chat:nuevo', chatNuevoHandler, [chatNuevoHandler]);

  const chatInvHandler = useCallback(
    (payload) => {
      const opId = payload?.operacion_id;
      const invId = payload?.inversionista_id;
      if (!opId || !invId || !payload?.mensaje) return;
      setConvInv((prev) => {
        const key = `${opId}_${invId}`;
        const existe = prev.some((c) => c.chat_key === key);
        if (!existe) {
          cargarInv();
          return prev;
        }
        const abierto =
          hiloInv &&
          String(hiloInv.operacion_id) === String(opId) &&
          String(hiloInv.inversionista_id) === String(invId);
        return prev.map((c) =>
          c.chat_key === key
            ? { ...c, ultimo: payload.mensaje, no_leidos: abierto ? 0 : (c.no_leidos || 0) + 1 }
            : c
        );
      });
      if (
        hiloInv &&
        String(hiloInv.operacion_id) === String(opId) &&
        String(hiloInv.inversionista_id) === String(invId)
      ) {
        setHiloInv((prev) => {
          const existe = (prev?.mensajes || []).some(
            (m) => String(m._id) === String(payload.mensaje._id)
          );
          if (existe) return prev;
          return { ...prev, mensajes: [...(prev?.mensajes || []), payload.mensaje] };
        });
        chatApi.marcarLeidasInv(opId, invId).catch(() => {});
      }
    },
    [hiloInv, cargarInv]
  );
  useSocketEvent('chat-inv:nuevo', chatInvHandler, [chatInvHandler]);

  const cerrarTodo = () => {
    setOpen(false);
    setHilo(null);
    setHiloInv(null);
  };

  const abrirHiloPyme = useCallback(async (op) => {
    setCanal('pyme');
    setHilo({ ...op, mensajes: [], estado: op.estado });
    setHiloCargando(true);
    setDraft('');
    setEditorKey((k) => k + 1);
    try {
      const res = await chatApi.getMensajes(op._id);
      setHilo(res.data);
      chatApi.marcarLeidas(op._id).catch(() => {});
      setConversaciones((prev) =>
        prev.map((c) => (String(c._id) === String(op._id) ? { ...c, no_leidos: 0 } : c))
      );
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setHilo(null);
    } finally {
      setHiloCargando(false);
    }
  }, []);

  const abrirHiloInv = useCallback(async (item) => {
    setCanal('inv');
    setHiloInv({
      operacion_id: item._id,
      inversionista_id: item.inversionista_id,
      estado: item.estado,
      mensajes: [],
    });
    setHiloInvCargando(true);
    setDraft('');
    setEditorKey((k) => k + 1);
    try {
      const res = await chatApi.getInvMensajes(item._id, item.inversionista_id);
      setHiloInv({ ...res.data, total_inversionistas: item.total_inversionistas });
      chatApi.marcarLeidasInv(item._id, item.inversionista_id).catch(() => {});
      setConvInv((prev) =>
        prev.map((c) =>
          c.chat_key === `${item._id}_${item.inversionista_id}` ? { ...c, no_leidos: 0 } : c
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setHiloInv(null);
    } finally {
      setHiloInvCargando(false);
    }
  }, []);

  const enviar = async () => {
    if (!draft.trim() || enviando) return;
    if (canalActivo === 'inv' && (!hiloInv || !hiloInv.inversionista_id)) return;
    if (canalActivo === 'pyme' && !hilo) return;
    setEnviando(true);
    const texto = draft.trim();
    try {
      let mensaje = null;
      if (canalActivo === 'inv') {
        const res = await chatApi.enviarInv(hiloInv.operacion_id, hiloInv.inversionista_id, texto);
        mensaje = res.data.mensaje;
        setHiloInv((prev) => {
          const msgs = prev?.mensajes || [];
          if (msgs.some((m) => String(m._id) === String(mensaje._id))) return prev;
          return { ...prev, mensajes: [...msgs, mensaje] };
        });
        setConvInv((prev) =>
          prev.map((c) =>
            c.chat_key === `${hiloInv.operacion_id}_${hiloInv.inversionista_id}`
              ? { ...c, ultimo: mensaje }
              : c
          )
        );
      } else {
        const res = await chatApi.enviarMensaje(hilo.operacion_id || hilo._id, texto);
        mensaje = res.data.mensaje;
        setHilo((prev) => {
          const msgs = prev?.mensajes || [];
          if (msgs.some((m) => String(m._id) === String(mensaje._id))) return prev;
          return { ...prev, mensajes: [...msgs, mensaje] };
        });
      }
      setDraft('');
      setEditorKey((k) => k + 1);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setEnviando(false);
    }
  };

  const handleMarcarObservada = async () => {
    const opId = hilo.operacion_id || hilo._id;
    if (!draft.trim()) {
      toast.error('Escribe un mensaje con tu observación antes de marcar como observada.');
      return;
    }
    if (!confirmarObs) {
      setConfirmarObs(true);
      setTimeout(() => setConfirmarObs(false), 6000);
      return;
    }
    setConfirmarObs(false);
    setMarcandoObs(true);
    try {
      await chatApi.marcarObservada(opId, draft.trim());
      setDraft('');
      setEditorKey((k) => k + 1);
      const res = await chatApi.getMensajes(opId);
      setHilo(res.data);
      cargarPyme();
      toast.success('Solicitud marcada como observada');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setMarcandoObs(false);
    }
  };

  const enviarTodos = async () => {
    if (!draft.trim() || enviando || !hiloInv) return;
    setEnviando(true);
    try {
      await chatApi.enviarInv(hiloInv.operacion_id, 'todos', draft.trim());
      setDraft('');
      setEditorKey((k) => k + 1);
      const res = await chatApi.getInvMensajes(hiloInv.operacion_id, hiloInv.inversionista_id);
      setHiloInv({ ...res.data, total_inversionistas: hiloInv.total_inversionistas });
      toast.success('Mensaje enviado a todos los inversionistas');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setEnviando(false);
    }
  };

  const handleRechazarPropuesta = async () => {
    const opId = hilo.operacion_id || hilo._id;
    if (!draft.trim()) {
      toast.error('Escribe un mensaje con el motivo antes de rechazar la propuesta.');
      return;
    }
    if (!confirmarRechazo) {
      setConfirmarRechazo(true);
      setTimeout(() => setConfirmarRechazo(false), 6000);
      return;
    }
    setConfirmarRechazo(false);
    setRechazando(true);
    try {
      await chatApi.rechazarPropuesta(opId, draft.trim());
      setDraft('');
      setEditorKey((k) => k + 1);
      const res = await chatApi.getMensajes(opId);
      setHilo(res.data);
      cargarPyme();
      toast.success('Propuesta rechazada');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setRechazando(false);
    }
  };

  if (!esParticipante) return null;

  const miAutorPyme = soyPyme ? 'pyme' : 'garante';
  const miAutorInv = soyInv ? 'inversionista' : 'garante';

  // ---- Views ----
  const viewListaPyme = (
    <>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
        {conversaciones.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              No tienes conversaciones abiertas por el momento.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {conversaciones.map((c) => (
              <ConversacionCard
                key={c._id}
                item={c}
                primario={nombrePyme(c)}
                secundario={c.ultimo?.mensaje}
                tipo={tipoTexto(c)}
                no_leidos={c.no_leidos || 0}
                activa={false}
                onClick={() => abrirHiloPyme(c)}
              />
            ))}
          </Stack>
        )}
      </Box>
    </>
  );

  const viewListaInv = (
    <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      {soyGarante && invVista === 'proyectos' ? (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <IconButton
              size="small"
              onClick={() => {
                setInvVista('lista');
                setInvSel(null);
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="subtitle1"
              noWrap
            >
              {invSel?.nombre || 'Proyectos'}
            </Typography>
          </Stack>
          <Stack spacing={2}>
            {proyectosDeInv.map((c) => (
              <ConversacionCard
                key={c.chat_key}
                item={c}
                primario={nombrePyme(c)}
                secundario={c.ultimo?.mensaje}
                tipo={tipoTexto(c)}
                no_leidos={c.no_leidos || 0}
                activa={false}
                onClick={() => abrirHiloInv(c)}
              />
            ))}
          </Stack>
        </Box>
      ) : (
        <>
          {soyGarante && (
            <Box sx={{ p: 1.5, pb: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Buscar inversionista..."
                value={busquedaInv}
                onChange={(e) => setBusquedaInv(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      fontSize="small"
                      color="disabled"
                    />
                  ),
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            {soyGarante ? (
              investorsAgrupados.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    No hay inversionistas con los que conversar.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {investorsAgrupados.map((inv) => (
                    <ConversacionCard
                      key={inv.inversionista_id}
                      item={{ estado: '', tipo_inversion: '' }}
                      primario={inv.nombre}
                      secundario={
                        inv.proyectos[0]?.ultimo?.mensaje || `${inv.proyectos.length} proyecto(s)`
                      }
                      tipo={`${inv.proyectos.length} proyecto${inv.proyectos.length === 1 ? '' : 's'}`}
                      no_leidos={inv.proyectosConNoLeidos || 0}
                      activa={false}
                      onClick={() => {
                        setInvSel({ inversionista_id: inv.inversionista_id, nombre: inv.nombre });
                        setInvVista('proyectos');
                      }}
                    />
                  ))}
                </Stack>
              )
            ) : convInv.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  No tienes conversaciones por el momento.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {convInv.map((c) => (
                  <ConversacionCard
                    key={c.chat_key}
                    item={c}
                    primario={nombrePyme(c)}
                    secundario={c.ultimo?.mensaje}
                    tipo={tipoTexto(c)}
                    no_leidos={c.no_leidos || 0}
                    activa={false}
                    onClick={() => abrirHiloInv(c)}
                  />
                ))}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const viewHiloPyme = hilo && (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <IconButton
          size="small"
          onClick={() => setHilo(null)}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            noWrap
          >
            {nombrePyme(hilo)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {tipoTexto(hilo)}
          </Typography>
        </Box>
        <Chip
          label={estadoLabels[hilo.estado] || hilo.estado}
          color={estadoColors[hilo.estado] || 'default'}
          size="small"
          variant="outlined"
        />
      </Stack>
      {soyGarante && ['en_revision', 'reenviado'].includes(hilo.estado) && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color={confirmarObs ? 'error' : 'warning'}
            disabled={marcandoObs}
            onClick={handleMarcarObservada}
          >
            {marcandoObs
              ? 'Marcando...'
              : confirmarObs
                ? '¿Confirmar? Al marcar observada la PYME deberá modificar y reenviar'
                : 'Marcar como observada'}
          </Button>
        </Box>
      )}
      {soyPyme && ['solicitado', 'propuesta_enviada'].includes(hilo.estado) && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color="error"
            disabled={rechazando}
            onClick={handleRechazarPropuesta}
          >
            {rechazando
              ? 'Enviando...'
              : confirmarRechazo
                ? '¿Confirmar? Rechazarás la propuesta'
                : 'Rechazar propuesta'}
          </Button>
        </Box>
      )}
      {hiloCargando ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={26} />
        </Box>
      ) : (
        <ThreadView
          mensajes={hilo?.mensajes}
          esMio={(m) => m.autor === miAutorPyme}
        />
      )}
      <ComposerView
        draft={draft}
        onChange={setDraft}
        onSend={enviar}
        sending={enviando}
        editorKey={editorKey}
        disabled={!hilo}
      />
    </>
  );

  const viewHiloInv = hiloInv && (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1, py: 1, borderBottom: 1, borderColor: 'divider' }}
      >
        <IconButton
          size="small"
          onClick={() => setHiloInv(null)}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            noWrap
          >
            {soyInv ? nombrePyme(hiloInv) : hiloInv.inversionista?.nombre || 'Inversionista'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
          >
            {soyInv ? tipoTexto(hiloInv) : `${nombrePyme(hiloInv)} · ${tipoTexto(hiloInv)}`}
          </Typography>
        </Box>
        <Chip
          label={estadoLabels[hiloInv.estado] || hiloInv.estado}
          color={estadoColors[hiloInv.estado] || 'default'}
          size="small"
          variant="outlined"
        />
      </Stack>
      {soyGarante && hiloInv && hiloInv.total_inversionistas > 1 && (
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color="info"
            disabled={enviando || !draft.trim()}
            onClick={enviarTodos}
          >
            Enviar a todos los inversionistas ({hiloInv.total_inversionistas})
          </Button>
        </Box>
      )}
      {hiloInvCargando ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={26} />
        </Box>
      ) : (
        <ThreadView
          mensajes={hiloInv?.mensajes}
          esMio={(m) => m.autor === miAutorInv}
        />
      )}
      <ComposerView
        draft={draft}
        onChange={setDraft}
        onSend={enviar}
        sending={enviando}
        editorKey={editorKey}
        disabled={!hiloInv}
      />
    </>
  );

  const mostrarTabs = soyGarante;
  const cuerpo =
    canalActivo === 'inv'
      ? hiloInv
        ? viewHiloInv
        : viewListaInv
      : hilo
        ? viewHiloPyme
        : viewListaPyme;

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        onClick={() => setOpen(true)}
        sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
      >
        <Badge
          badgeContent={totalNoLeidos}
          color="error"
        >
          <ChatIcon />
        </Badge>
      </Fab>
      <Drawer
        anchor="right"
        open={open}
        onClose={cerrarTodo}
        PaperProps={{ sx: { width: { xs: '100%', sm: 400 }, maxWidth: '100%' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="h6">Mensajes</Typography>
            <IconButton
              size="small"
              onClick={cerrarTodo}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          {mostrarTabs && !hilo && !hiloInv && (
            <Box sx={{ px: 2, py: 1 }}>
              <Stack
                direction="row"
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 999,
                  bgcolor: 'background.paper',
                  p: 0.25,
                }}
              >
                <Button
                  size="small"
                  fullWidth
                  variant={canal === 'pyme' ? 'contained' : 'text'}
                  onClick={() => {
                    setCanal('pyme');
                    cargarPyme();
                  }}
                  sx={{ borderRadius: 999 }}
                >
                  PYME
                  <TabConteo
                    count={pymesConNoLeidos}
                    activo={canal === 'pyme'}
                  />
                </Button>
                <Button
                  size="small"
                  fullWidth
                  variant={canal === 'inv' ? 'contained' : 'text'}
                  onClick={() => {
                    setCanal('inv');
                    cargarInv();
                  }}
                  sx={{ borderRadius: 999 }}
                >
                  Inversionistas
                  <TabConteo
                    count={inversionistasConNoLeidos}
                    activo={canal === 'inv'}
                  />
                </Button>
              </Stack>
            </Box>
          )}
          {cuerpo}
        </Box>
      </Drawer>
    </>
  );
};
